import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { injectIntl, defineMessages } from 'react-intl';
import uniqueid from 'lodash.uniqueid';
import Button from '../../../../components/formControls/Button';
import FormSectionTitle from '../../../../components/FormSectionTitle';
import Input from '../../../../components/formControls/Input';
import ToastMessage from '../../../../components/messages/ToastMessage';
import HelpButton from '../../../../components/HelpButton';
import Icon from '../../../../components/icons/Icon';
import { getVirtualOperatorSettingsClosing, isSaveClosingSettingsError, isSaveClosingSettingsLoaded, isSaveClosingSettingsSuccess, isSaveFestivitySettingsError, isSaveFestivitySettingsLoaded, isSaveFestivitySettingsSuccess } from '../../../../js/virtualOperator/selectors';
import { saveVirtualOperatorSettingsClosingRequest } from '../../../../js/virtualOperator/actions';
import DatePickerInput from '../../../../components/formControls/DatePickerInput';
import Label from '../../../../components/formControls/Label';

const messages = defineMessages({
  startDay: {
    id: 'PbxVirtualOperatorSettingsClosing.startDay',
    defaultMessage: 'From',
  },
  endDay: {
    id: 'PbxVirtualOperatorSettingsClosing.endDay',
    defaultMessage: 'To',
  },
  description: {
    id: 'PbxVirtualOperatorSettingsClosing.description',
    defaultMessage: 'Description',
  },
  title: {
    id: 'PbxVirtualOperatorSettingsClosing.title',
    defaultMessage: 'Closing days',
  },
  buttonSave: {
    id: 'PbxVirtualOperatorSettingsClosing.button.save',
    defaultMessage: 'Save',
  },
  tooltipRemove: {
    id: 'PbxVirtualOperatorSettingsClosing.tooltip.remove',
    defaultMessage: 'Remove',
  },
  tooltipAdd: {
    id: 'PbxVirtualOperatorSettingsClosing.tooltip.add',
    defaultMessage: 'Add',
  },
  errorDate: {
    id: 'PbxVirtualOperatorSettingsClosing.error.errorDate',
    defaultMessage: 'Please set valid date',
  },
  errorDescription: {
    id: 'PbxVirtualOperatorSettingsClosing.error.errorDescription',
    defaultMessage: 'Please set valid description',
  },
  errorDescriptionDuplicate: {
    id: 'PbxVirtualOperatorSettingsClosing.error.errorDescriptionDuplicate',
    defaultMessage: 'Description already exists',
  },
  saveError: {
    id: 'PbxVirtualOperatorSettingsClosing.saveError',
    defaultMessage: 'Saving error',
  },
  saveSuccess: {
    id: 'PbxVirtualOperatorSettingsClosing.saveSuccess',
    defaultMessage: 'Saving successfully performed',
  },
  info: {
    id: 'PbxVirtualOperatorSettingsClosing.info',
    defaultMessage:
      'Add here closing date range to be considered as festivities in Enterprise and Offices sections',
  },
});

const addedEmpty = { from: '', to: '', description: '' };

class PbxVirtualOperatorSettingsClosing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      added: props.closing && props.closing.length ? props.closing.map(item => ({ ...item, from: moment(item.from, 'DD/MM/YYYY').toDate(), to: moment(item.to, 'DD/MM/YYYY').toDate() })) : [addedEmpty],
      errors: props.closing && props.closing.length ? props.closing.map(() => ({})) : [{}],
      formErrors: false,
    };
  }

  onSubmit = (e) => {
    e.preventDefault();
    const {
      save,
      intl: { formatMessage },
    } = this.props;
    const { added } = this.state;
    const errors = {};

    const addedFiltered = added.filter(
      (o) => o.from || o.to || o.description !== ''
    );
    addedFiltered.forEach((o, idx) => {
      if (o.from === '' || o.to === '') {
        errors[idx] = {};
        errors[idx].dates = formatMessage(messages.errorDate);
      }
      if (moment(o.from).isAfter(moment(o.to))) {
        errors[idx] = {};
        errors[idx].dates = formatMessage(messages.errorDate);
      }
      if (o.description === '') {
        if (!errors[idx]) errors[idx] = {};
        errors[idx].description = formatMessage(messages.errorDescription);
      }
      if (addedFiltered.some((item, idx2) => item.description === o.description && idx !== idx2)) {
        if (!errors[idx]) errors[idx] = {};
        errors[idx].description = formatMessage(messages.errorDescriptionDuplicate);
      }
    });
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
    } else {
      save({
        closing: addedFiltered.map(item => ({
          from: moment(item.from).format('DD/MM/YYYY'),
          to: moment(item.to).format('DD/MM/YYYY'),
          description: item.description,
        }))
      });
    }
  };

  handleChangeDescription = (description, idx) => {
    const { added, errors } = this.state;
    if (added.length > 0) {
      added.splice(idx, 1, {
        from: added[idx].from,
        to: added[idx].to,
        description,
      });
      errors[idx] = {};
    } else {
      added.push({ from: '', to: '', description });
    }

    this.setState({
      added,
      errors,
    });
  };

  handleStart = (day, idx) => {
    const { added, errors } = this.state;
    if (added.length > 0) {
      added.splice(idx, 1, {
        from: day,
        to: added[idx].to,
        description: added[idx].description,
      });
      errors[idx] = {};
    } else {
      added.push({ from: day, to: '', description: '' });
    }
    this.setState({
      added,
      errors,
    });
  };

  handleEnd = (day, idx) => {
    const { added, errors } = this.state;
    if (added.length > 0) {
      added.splice(idx, 1, {
        from: added[idx].from,
        to: day,
        description: added[idx].description,
      });
      errors[idx] = {};
    } else {
      added.push({ from: '', to: day, description: '' });
    }
    this.setState({
      added,
      errors,
    });
  };

  handleRemove = (idx) => {
    const { added, errors } = this.state;
    added.splice(idx, 1);
    errors.splice(idx, 1);
    this.setState({
      added,
      errors,
    });
  };

  handleAdd = () => {
    this.setState({ added: [...this.state.added, addedEmpty], errors: [...this.state.errors, {}] });
  };

  render() {
    const {
      saving,
      savingError,
      savingSuccess,
      intl: { formatMessage },
    } = this.props;

    const { added, errors, formErrors } = this.state;
    return (
      <>
        <FormSectionTitle className="mt-3 mb-1">
          {formatMessage(messages.title)}
        </FormSectionTitle>
        {savingError && (
          <ToastMessage
            type="danger"
            text={formatMessage(messages.saveError)}
            messageCode={savingError}
          />
        )}
        {savingSuccess && (
          <ToastMessage
            type="success"
            text={formatMessage(messages.saveSuccess)}
            messageCode={savingSuccess}
          />
        )}
        {formErrors && (
          <ToastMessage
            type="danger"
            text={formatMessage(messages.errorCheckFields)}
            onClose={() => this.setState({ formErrors: false })}
          />
        )}
        <div className="font-weight-light p-1 pb-3">
          {formatMessage(messages.info)}
        </div>
        <div className="px-2">
          {added.map((item, idx) => {
            return (
              <div className="row no-gutters" key={idx}>
                <div className="col-auto">
                  <div className="d-flex">                    
                    <Label for="startDay" className='mr-1' style={{lineHeight: '35px'}} >
                      {formatMessage(messages.startDay)}
                    </Label>
                    <DatePickerInput
                      clearable
                      day={item.from}
                      name="startDay"
                      error={errors[idx].dates}
                      onChange={(day) => this.handleStart(day, idx)}
                    />
                    <Label for="endDay" className='ml-2 mr-1' style={{lineHeight: '35px'}} >
                      {formatMessage(messages.endDay)}
                    </Label>
                    <DatePickerInput
                      clearable
                      day={item.to}
                      name="endDay"
                      error={errors[idx].dates}
                      onChange={(day) => this.handleEnd(day, idx)}
                    />                    
                  </div>
                </div>
                <div className="col pl-2">
                  <Input
                    type="text"
                    id={uniqueid('FestivitiesAddedDescription-')}
                    name="ClosingAddedDescription"
                    value={item.description}
                    menuPlacement="top"
                    placeholder={formatMessage(messages.description)}
                    onChange={(e) =>
                      this.handleChangeDescription(e.target.value, idx)
                    }
                    error={errors[idx] && errors[idx].description}
                  />
                </div>
                <div className="col-auto pt-2">
                  <>
                    {added && added.length > 0 && (
                      <span
                        className="text-center"
                        onClick={this.handleAdd}
                      >
                        <Icon
                          className="ml-2 text-center"
                          name="add-plus"
                          width={16}
                          height={16}
                          color="var(--yn-blue-dark)"
                          tooltipPosition="right"
                        >
                          {formatMessage(messages.tooltipAdd)}
                        </Icon>
                      </span>
                    )}
                    {added && added.length > 0 && (
                      <span
                        className="text-center"
                        onClick={() => this.handleRemove(idx)}
                      >
                        <Icon
                          className="ml-2 text-center"
                          name="remove-minus"
                          width={16}
                          height={16}
                          color="var(--yn-blue-dark)"
                          tooltipPosition="right"
                        >
                          {formatMessage(messages.tooltipRemove)}
                        </Icon>
                      </span>
                    )}
                  </>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pt-3 text-center">
          <Button
            className="btn btn-primary"
            loading={saving}
            text={formatMessage(messages.buttonSave)}
            onClick={this.onSubmit}
          />
        </div>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    saving: !isSaveClosingSettingsLoaded(state),
    savingError: isSaveClosingSettingsError(state),
    savingSuccess: isSaveClosingSettingsSuccess(state),
    closing: getVirtualOperatorSettingsClosing(state),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    save: saveVirtualOperatorSettingsClosingRequest,
  })(PbxVirtualOperatorSettingsClosing)
);
